import type { Device } from "@gooner/shared";

/**
 * What a screen at /view/d/:id is doing.
 *
 *   resolving     — nothing showable YET: the first GET /devices/:id has not answered, or
 *                   it answered with a device the server has not finished pointing
 *   unjoined      — this id is not a device; the way back is the four-character code
 *   playing       — a device with a view and a live socket
 *   disconnected  — a device with a view whose socket has dropped
 *
 * THERE IS NO "waiting" ANY MORE, and its absence is the point. It named a device with
 * nothing assigned to it, and it was reachable by the most ordinary act in the product —
 * pressing "Or use this device as the screen" on the code card — so the reward for turning
 * a browser into a screen was a black page reading "Waiting for a view". The server now
 * guarantees every device it hands out is pointed at something (see `ensureShowing` in
 * server/src/routes/devices.ts), so the gap is a beat between two polls rather than a
 * place a television can sit all evening. A beat is `resolving`.
 */
type ScreenState = "resolving" | "unjoined" | "playing" | "disconnected";

export function resolveScreenState({
  device,
  missing,
  connected,
  viewMissing = false,
}: {
  /** null while the first GET /devices/:id is still out. */
  device: Device | null;
  /** true once the server has said this device id is not a device (a 404, not a blip). */
  missing: boolean;
  /** the display socket's status; ignored unless there is a view to show. */
  connected: boolean;
  /**
   * true once the server has said the view this device points at is not a view.
   *
   * `GET /devices/:id` returns `showing.viewId` with no existence check, and a temporary
   * view evicts on its TTL without unpointing the devices showing it — so a non-null
   * `showing` is not proof there is anything to show.
   */
  viewMissing?: boolean;
}): ScreenState {
  // The row is gone, whatever we last read off it. Checked first so a stale object from
  // a previous poll can't keep a deleted screen "playing".
  if (missing) return "unjoined";
  if (!device) return "resolving";
  // No view — or a view id that no longer resolves — means there is nothing this screen
  // can show yet and no socket was usefully opened, so there is no connection to have
  // lost; reporting "disconnected" here would invent a fault out of a screen that is
  // simply still being pointed. `resolving` is also the state that KEEPS POLLING, which is
  // what makes this self-healing: the next GET /devices/:id runs ensureShowing on the
  // server and comes back with a quick setup.
  if (!device.showing || viewMissing) return "resolving";
  return connected ? "playing" : "disconnected";
}
